import React, { useState, useEffect, useRef } from 'react';
import { ChatMessage, Advisor, RolePlayFeedback } from '../types';
import { RobotIcon, Spinner, UserGroupIcon as UserIcon, CheckCircleIcon, PencilIcon, MicrophoneIcon } from '../components/icons';
import { api } from '../services/api';

// Type definitions for Web Speech API as they are not standard in TS DOM lib yet.
interface SpeechRecognitionAlternative {
    readonly transcript: string;
    readonly confidence: number;
}
interface SpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    item(index: number): SpeechRecognitionAlternative;
    [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
    readonly length: number;
    item(index: number): SpeechRecognitionResult;
    [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
    readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
    readonly error: string;
}
interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    start(): void;
    stop(): void;
    onresult: (event: SpeechRecognitionEvent) => void;
    onerror: (event: SpeechRecognitionErrorEvent) => void;
}


interface RolePlayViewProps {
  scenario: string;
  scenarioTitle: string;
  user: Advisor;
  onFinish: () => void;
}

const RolePlayView: React.FC<RolePlayViewProps> = ({ scenario, scenarioTitle, user, onFinish }) => {
  const [chatReady, setChatReady] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  type PageState = 'chatting' | 'generating_feedback' | 'showing_feedback';
  const [pageState, setPageState] = useState<PageState>('chatting');
  const [feedback, setFeedback] = useState<RolePlayFeedback | null>(null);

  // --- Audio Interaction State ---
  const [interactionMode, setInteractionMode] = useState<'text' | 'audio'>('text');
  const [isRecording, setIsRecording] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);


  useEffect(() => {
    // --- Speech Recognition & Synthesis Setup ---
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition && window.speechSynthesis) {
        setSpeechSupported(true);
        const recognition: SpeechRecognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'es-ES';

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let transcript = '';
            for (let i = 0; i < event.results.length; i++) {
                transcript += event.results[i][0].transcript;
            }
            setInput(transcript);
        };
        
        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
            console.error('Speech recognition error', event.error);
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                 setError('Permiso para micrófono denegado. Habilítalo en los ajustes de tu navegador.');
            }
            setIsRecording(false);
        };

        recognitionRef.current = recognition;
    }

    // --- Chat Initialization ---
    // Gemini now runs through the backend so the frontend does not depend on a
    // build-time API key embedded in the browser bundle.
    setChatReady(true);
    setIsLoading(false);
    
    return () => {
      window.speechSynthesis.cancel();
      if(recognitionRef.current) {
        recognitionRef.current.stop();
      }
    }
  }, [scenario, user.name]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const speak = (text: string) => {
    if (!speechSupported || !text.trim()) return;

    const getVoicesAndSpeak = () => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'es-ES';
        const voices = window.speechSynthesis.getVoices();
        const spanishVoice = voices.find(v => v.lang === 'es-ES' && (v.name.includes('Google') || v.name.includes('Elena') || v.name.includes('Paulina')));
        if (spanishVoice) {
            utterance.voice = spanishVoice;
        } else {
            const genericSpanishVoice = voices.find(v => v.lang.startsWith('es-'));
            if(genericSpanishVoice) utterance.voice = genericSpanishVoice;
        }
        window.speechSynthesis.speak(utterance);
    };

    if (window.speechSynthesis.getVoices().length === 0) {
        window.speechSynthesis.onvoiceschanged = getVoicesAndSpeak;
    } else {
        getVoicesAndSpeak();
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !chatReady) return;

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    }
    window.speechSynthesis.cancel();

    const userMessage: ChatMessage = { sender: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    setMessages(prev => [...prev, { sender: 'ai', text: '' }]);

    try {
        const history = [...messages, userMessage];
        const { text: fullResponse } = await api.generateRolePlayResponse({
            advisorName: user.name,
            scenario,
            messages: history,
        });
        setMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = { sender: 'ai', text: fullResponse };
            return newMessages;
        });
        if (interactionMode === 'audio') {
            speak(fullResponse);
        }
    } catch (err) {
      console.error("Error sending message to AI:", err);
      const errorMessage = err instanceof Error ? `Lo siento, no pude procesar tu respuesta: ${err.message}` : 'Lo siento, no pude procesar tu respuesta. Intenta de nuevo.';
      setMessages(prev => {
         const newMessages = [...prev];
         newMessages[newMessages.length - 1] = { sender: 'ai', text: errorMessage };
         return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFinishPractice = async () => {
    if (messages.length < 2) {
        setError("Debes interactuar con el cliente antes de finalizar la práctica.");
        return;
    }
    setPageState('generating_feedback');
    setError(null);
    window.speechSynthesis.cancel();
    if (isRecording) {
        recognitionRef.current?.stop();
        setIsRecording(false);
    }


    const transcript = messages.map(msg => `${msg.sender === 'user' ? user.name : 'Cliente'}: ${msg.text}`).join('\n');
    
    const feedbackPrompt = `Eres un coach de ventas experto para asesores de seguros. Analiza la siguiente transcripción de una conversación de role-play entre el asesor '${user.name}' y un cliente potencial. El escenario era: "${scenario}". Evalúa el desempeño del asesor de manera constructiva y proporciona una puntuación, puntos fuertes, áreas de mejora y un resumen.

Aquí está la transcripción:
${transcript}

Por favor, evalúa el desempeño del asesor y proporciona tu retroalimentación en el formato JSON solicitado.`;

    try {
        const feedbackJson = await api.generateRolePlayFeedback({ prompt: feedbackPrompt });
        setFeedback(feedbackJson);
        setPageState('showing_feedback');

        // Save session to history
        try {
            await api.saveRolePlaySession({
                advisorId: user.id,
                date: Date.now(),
                scenarioTitle,
                scenario,
                transcript: messages,
                feedback: feedbackJson,
            });
        } catch (saveError) {
            console.error("Failed to save role play session:", saveError);
            // Non-critical error, don't block user flow
        }

    } catch (e) {
        console.error("Error generating feedback:", e);
        setError(e instanceof Error ? `Lo siento, no se pudo generar la retroalimentación: ${e.message}` : 'Lo siento, no se pudo generar la retroalimentación. Por favor, inténtalo de nuevo más tarde.');
        setPageState('chatting');
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) return;
    if (isRecording) {
        recognitionRef.current.stop();
        setIsRecording(false);
    } else {
        setInput('');
        recognitionRef.current.start();
        setIsRecording(true);
    }
  };


  const renderChat = () => (
    <>
      <main className="flex-1 flex flex-col p-4 sm:p-6 overflow-y-auto">
          <div className="bg-slate-700 border-l-4 border-purple-400 text-purple-100 p-4 rounded-r-lg mb-6" role="alert">
              <p className="font-bold text-white">Tu Escenario</p>
              <p>{scenario}</p>
          </div>
          <div className="flex-1 space-y-4">
              {messages.map((msg, index) => (
                  <div key={index} className={`flex items-end gap-2 sm:gap-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.sender === 'ai' && <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white"><RobotIcon className="h-5 w-5"/></div>}
                      <div className={`max-w-md lg:max-w-2xl px-4 py-2 rounded-xl shadow-sm ${msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-gray-200'}`}>
                          {msg.text ? <p className="whitespace-pre-wrap">{msg.text}</p> : <Spinner className="h-4 w-4 text-gray-400" />}
                      </div>
                      {msg.sender === 'user' && <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white"><UserIcon className="h-5 w-5"/></div>}
                  </div>
              ))}
              <div ref={messagesEndRef} />
          </div>
      </main>
      <footer className="bg-slate-800 border-t border-slate-700 p-4 space-y-3">
            {speechSupported && (
                <div className="flex items-center justify-center space-x-4">
                    <span className="text-sm text-gray-400">Modo de Interacción:</span>
                    <div className="flex items-center bg-slate-700 rounded-full p-1">
                        <button onClick={() => setInteractionMode('text')} className={`px-3 py-1 text-sm font-semibold rounded-full transition-colors ${interactionMode === 'text' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-slate-600'}`}>Texto</button>
                        <button onClick={() => setInteractionMode('audio')} className={`px-3 py-1 text-sm font-semibold rounded-full transition-colors ${interactionMode === 'audio' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-slate-600'}`}>Voz</button>
                    </div>
                </div>
            )}
          <form onSubmit={handleSendMessage} className="container mx-auto flex items-center space-x-2 sm:space-x-4">
              <div className="relative flex-1">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.currentTarget.value)}
                    placeholder={error ? "No se pudo iniciar el chat" : "Escribe tu respuesta aquí..."}
                    disabled={isLoading || !chatReady || !!error}
                    className="w-full p-3 border border-slate-600 bg-slate-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-slate-600 disabled:cursor-not-allowed"
                    aria-label="Chat input"
                />
                {interactionMode === 'audio' && speechSupported && (
                    <button 
                        type="button" 
                        onClick={toggleRecording} 
                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-colors ${isRecording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-slate-600 hover:bg-slate-500'} text-white`}
                        aria-label={isRecording ? 'Detener grabación' : 'Iniciar grabación'}
                    >
                        <MicrophoneIcon className="h-5 w-5" />
                    </button>
                )}
              </div>
              <button type="submit" disabled={isLoading || !input.trim() || !chatReady} className="bg-blue-600 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors">
                  Enviar
              </button>
          </form>
      </footer>
    </>
  );

  const renderFeedbackLoading = () => (
    <main className="flex-1 flex flex-col justify-center items-center text-center p-6 animate-fade-in">
        <Spinner className="h-16 w-16 text-purple-500 mb-4" />
        <h2 className="text-2xl font-semibold text-white">Analizando tu desempeño...</h2>
        <p className="text-gray-400 mt-2">Tu coach de IA está preparando tu retroalimentación.</p>
    </main>
  );

  const renderFeedback = () => {
    if (!feedback) return renderChat();

    const scoreColor = feedback.overallScore >= 80 ? 'text-green-500' : feedback.overallScore >= 60 ? 'text-yellow-500' : 'text-red-500';

    return (
        <main className="flex-1 p-6 sm:p-8 bg-slate-900 overflow-y-auto animate-fade-in">
            <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-white text-center">Retroalimentación de la Práctica</h2>
                
                <div className="my-8 text-center">
                    <p className="text-lg text-gray-400">Tu Puntuación General</p>
                    <p className={`text-7xl font-bold ${scoreColor}`}>{feedback.overallScore}<span className="text-3xl text-gray-400">%</span></p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-slate-800 p-6 rounded-lg shadow-md border border-slate-700">
                        <h3 className="text-xl font-semibold text-green-400 flex items-center mb-4">
                            <CheckCircleIcon className="h-6 w-6 mr-2 text-green-500" />
                            Puntos Fuertes
                        </h3>
                        <ul className="space-y-3 list-disc list-inside text-gray-300">
                            {feedback.strengths.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                    </div>
                    <div className="bg-slate-800 p-6 rounded-lg shadow-md border border-slate-700">
                        <h3 className="text-xl font-semibold text-yellow-400 flex items-center mb-4">
                            <PencilIcon className="h-6 w-6 mr-2 text-yellow-500" />
                            Áreas de Mejora
                        </h3>
                        <ul className="space-y-3 list-disc list-inside text-gray-300">
                            {feedback.areasForImprovement.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                    </div>
                </div>

                <div className="mt-8 bg-slate-800 p-6 rounded-lg shadow-md border border-slate-700">
                    <h3 className="text-xl font-semibold text-white mb-3">Resumen del Coach</h3>
                    <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{feedback.summary}</p>
                </div>
                
                <div className="mt-8 text-center">
                    <button onClick={onFinish} className="bg-purple-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-purple-700 transition-colors">
                        Practicar de Nuevo
                    </button>
                </div>
            </div>
        </main>
    );
  };

  return (
    <div className="bg-slate-900 flex flex-col h-full rounded-lg border border-slate-700">
        <header className="bg-slate-800 shadow-sm flex-shrink-0 z-10 border-b border-slate-700 rounded-t-lg">
            <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                <div className="flex-1"></div>
                <div className="text-center flex-1">
                    <h1 className="text-lg sm:text-xl font-bold text-white truncate">{scenarioTitle}</h1>
                </div>
                <div className="flex-1 text-right">
                   {pageState === 'chatting' && (
                     <button onClick={handleFinishPractice} disabled={isLoading || messages.length < 2} className="bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm disabled:bg-green-400 disabled:cursor-not-allowed">
                         Finalizar y Obtener Feedback
                     </button>
                   )}
                </div>
            </div>
        </header>
        {error && (
            <div className="bg-red-900 border-b-2 border-red-700 text-red-200 p-3 text-center">
                <p>{error}</p>
            </div>
        )}

        {pageState === 'chatting' && renderChat()}
        {pageState === 'generating_feedback' && renderFeedbackLoading()}
        {pageState === 'showing_feedback' && renderFeedback()}
    </div>
  );
};

export default RolePlayView;